/**
 * List of Google client libraries that we support.
 */
export interface GoogleClientInfo {
    /**
     * The URL to the Google client script.
     */
    script: string;
    /**
     * The name of the global variable that the client script will create.
     */
    module: string;
}

export enum GoogleClient {
    GAPI = "GAPI",
    GSI = "GSI",
}

/**
 * Libdefs for the non-npm Google Identity Services (GSI) library.
 * @see https://developers.google.com/identity/gsi/web/reference/js-reference
 * Adapted from:
 * https://github.com/DefinitelyTyped/DefinitelyTyped/blob/master/types/google.accounts/index.d.ts
 */

/**
 * A TokenResponse JavaScript object will be passed to your callback
 * method (as defined in TokenClientConfig) in the popup UX.
 */
export interface TokenResponse {
    /**
     * The access token of a successful token response.
     */
    access_token: string;
    /**
     * The lifetime in seconds of the access token.
     */
    expires_in: number;
    /**
     * The hosted domain the signed-in user belongs to.
     */
    hd: string;
    /**
     * The prompt value that was used from the possible list of values
     * specified by TokenClientConfig or OverridableTokenClientConfig.
     */
    prompt: string;
    /**
     * The type of the token issued.
     */
    token_type: string;
    /**
     * A space-delimited list of scopes that are approved by the user.
     */
    scope: string;
    /**
     * The string value that your application uses to maintain state
     * between your authorization request and the response.
     */
    state: string;
    /**
     * A single ASCII error code.
     */
    error: string;
    /**
     * Human-readable ASCII text providing additional information, used
     * to assist the client developer in understanding the error that
     * occurred.
     */
    error_description: string;
    /**
     * A URI identifying a human-readable web page with information
     * about the error, used to provide the client developer with
     * additional information about the error.
     */
    error_uri: string;
}

export interface ClientConfigError {
    message: string;
    stack?: string;
    type: "unknown" | "popup_closed" | "popup_failed_to_open";
}

/**
 * A CodeResponse JavaScript object will be passed to your callback
 * method (as defined in CodeClientConfig) in the popup UX.
 */
export interface CodeResponse {
    /**
     * The authorization code of a successful token response.
     */
    code: string;
    /**
     * A space-delimited list of scopes that are approved by the user.
     */
    scope: string;
    /**
     * The string value that your application uses to maintain state
     * between your authorization request and the response.
     */
    state: string;
    /**
     * A single ASCII error code.
     */
    error: string;
    /**
     * Human-readable ASCII text providing additional information,
     * used to assist the client developer in understanding
     * the error that occurred.
     */
    error_description: string;
    /**
     * A URI identifying a human-readable web page with information
     * about the error, used to provide the client developer with
     * additional information about the error.
     */
    error_uri: string;
}

interface CodeClientConfig {
    /**
     * Required. The client ID for your application. You can find this
     * value in the API Console.
     */
    client_id: string;
    /**
     * Required. A space-delimited list of scopes that identify the
     * resources that your application could access on the user's
     * behalf. These values inform the consent screen that Google
     * displays to the user.
     */
    scope: string;
    /**
     * Required for redirect UX. Determines where the API server
     * redirects the user after the user completes the authorization
     * flow. The value must exactly match one of the authorized redirect
     * URIs for the OAuth 2.0 client, which you configured in the API
     * Console and must conform to our Redirect URI validation rules.
     * The property will be ignored by the popup UX.
     */
    redirect_uri?: string;
    /**
     * Required for popup UX. The JavaScript function name that handles
     * returned code response. The property will be ignored by the
     * redirect UX.
     */
    callback?: (response: CodeResponse) => void;
    /**
     * Optional. Recommended for redirect UX. Specifies any string value
     * that your application uses to maintain state between your
     * authorization request and the authorization server's response.
     */
    state?: string;
    /**
     * Optional, defaults to true. If set to false, more granular Google
     * Account permissions will be disabled for clients created before
     * 2019. No effect for newer clients, since more granular
     * permissions is always enabled for them.
     */
    enable_serial_consent?: boolean;
    /**
     * Optional. If your application knows which user should authorize
     * the request, it can use this property to provide a hint to
     * Google. The email address for the target user. For more
     * information, see the login_hint field in the OpenID Connect docs.
     */
    hint?: string;
    /**
     * Optional. If your application knows the Workspace domain the user
     * belongs to, use this to provide a hint to Google. For more
     * information, see the hd field in the OpenID Connect docs.
     */
    hosted_domain?: string;
    /**
     * Optional. The UX mode to use for the authorization flow. By
     * default, it will open the consent flow in a popup. Valid values
     * are popup and redirect.
     */
    ux_mode?: "popup" | "redirect";
    /**
     * Optional, defaults to 'false'. Boolean value to prompt the user
     * to select an account.
     */
    select_account?: boolean;
    /**
     * Optional. The JavaScript function that handles some non-OAuth
     * errors, such as the popup window is failed to open; or closed
     * before an OAuth response is returned.
     */
    error_callback?: (error: ClientConfigError) => void;
}

interface TokenClientConfig {
    /**
     * Required.
     * The client ID for your application. You can find this value in
     * the API Console.
     */
    client_id: string;
    /**
     * Required. The JavaScript function name that handles returned
     * token response.
     */
    callback: (tokenResponse: TokenResponse) => void;
    /**
     * Optional.
     * A space-delimited list of scopes that identify the resources that
     * your application could access on the user's behalf. These values
     * inform the consent screen that Google displays to the user.
     */
    scope?: string;
    /**
     * Optional, defaults to 'select_account'.
     * A space-delimited, case-sensitive list of prompts to present the
     * user.
     * Possible values are:
     * empty string The user will be prompted only the first time your
     *     app requests access. Cannot be specified with other values.
     * 'none' Do not display any authentication or consent screens. Must
     *     not be specified with other values.
     * 'consent' Prompt the user for consent.
     * 'select_account' Prompt the user to select an account.
     */
    prompt?: "" | "none" | "consent" | "select_account";
    /**
     * Optional, defaults to true.
     * If set to false, more granular Google Account permissions will be
     * disabled for clients created before 2019. No effect for newer
     * clients, since more granular permissions is always enabled for
     * them.
     */
    enable_serial_consent?: boolean;
    /**
     * Optional.
     * If your application knows which user should authorize the
     * request, it can use this property to provide a hint to Google.
     * The email address for the target user. For more information, see
     * the login_hint field in the OpenID Connect docs.
     */
    hint?: string;
    /**
     * Optional.
     * If your application knows the Workspace domain the user belongs
     * to, use this to provide a hint to Google. For more information,
     * see the hd field in the OpenID Connect docs.
     */
    hosted_domain?: string;
    /**
     * Optional. Not recommended.
     * Specifies any string value that your application uses to maintain
     * state between your authorization request and the authorization
     * server's response.
     */
    state?: string;
    /**
     * Optional. The JavaScript function that handles some non-OAuth
     * errors, such as the popup window is failed to open; or closed
     * before an OAuth response is returned.
     */
    error_callback?: (error: ClientConfigError) => void;
}

interface OverridableTokenClientConfig {
    /**
     * Optional. A space-delimited, case-sensitive list of prompts to
     * present the user.
     */
    prompt?: string;
    /**
     * Optional. If set to false, more granular Google Account
     * permissions will be disabled for clients created before 2019. No
     * effect for newer clients, since more granular permissions is
     * always enabled for them.
     */
    enable_serial_consent?: boolean;
    /**
     * Optional. If your application knows which user should authorize
     * the request, it can use this property to provide a hint to
     * Google. The email address for the target user. For more
     * information, see the login_hint field in the OpenID Connect docs.
     */
    hint?: string;
    /**
     * Optional. Not recommended. Specifies any string value that your
     * application uses to maintain state between your authorization
     * request and the authorization server's response.
     */
    state?: string;
}

export interface TokenClient {
    /**
     * starts the OAuth 2.0 Token UX flow
     */
    requestAccessToken: (overrideConfig?: OverridableTokenClientConfig) => void;
}

export interface CodeClient {
    /**
     * starts the OAuth 2.0 Code UX flow.
     */
    requestCode: () => void;
}

export interface GoogleIdentityServices {
    accounts: {
        oauth2: {
            /**
             * Initializes and returns a code client, with the configurations in the
             * parameter.
             */
            initCodeClient(config: CodeClientConfig): CodeClient;
            /**
             * Initializes and returns a token client, with the configurations
             * in the parameter.
             */
            initTokenClient(config: TokenClientConfig): TokenClient;
            /**
             * Checks if the user granted all the specified scope or scopes.
             */
            hasGrantedAllScopes(
                tokenResponse: TokenResponse,
                firstScope: string,
                ...restScopes: Array<string>
            ): boolean;
        };
    };
}

/**
 * A check on whether an object satisfies the GoogleIdentityServices type at
 * runtime.
 *
 * @param x the object that may satisfy GoogleIdentityServices
 * @returns a boolean for whether x satisfies GoogleIdentityServices
 */
export function isGoogleIdentityServices(
    x: unknown,
): x is GoogleIdentityServices {
    if (!(typeof x === "object" && x !== null)) {
        return false;
    }
    const known = x as any;

    if (
        !(
            typeof known.accounts === "object" &&
            !!known.accounts &&
            typeof known.accounts.oauth2 === "object" &&
            !!known.accounts.oauth2
        )
    ) {
        return false;
    }
    const hasRequiredFunctions =
        typeof known.accounts.oauth2.initCodeClient === "function" &&
        typeof known.accounts.oauth2.initTokenClient === "function" &&
        typeof known.accounts.oauth2.hasGrantedAllScopes === "function";

    return hasRequiredFunctions;
}
